let isGoogleAnalyticsLoaded = false

const GOOGLE_ANALYTICS_SCRIPT_ID = 'google-analytics-gtag'
const GOOGLE_ANALYTICS_COOKIE_NAME_REGEX = /^_g(?:a(?:_.+)?|id(?:_.+)?|at(?:_.+)?)$/

/**
 * isGoogleAnalyticsCookieName
 * @param {string} cookieName Cookie name
 * @returns {boolean} Is Google Analytics cookie name
 */
function isGoogleAnalyticsCookieName(cookieName) {
    return GOOGLE_ANALYTICS_COOKIE_NAME_REGEX.test(cookieName)
}

/**
 * getCookieDomainCandidates
 * @returns {string[]} Cookie domain candidates
 */
function getCookieDomainCandidates() {
    if (typeof window === 'undefined' || !window.location?.hostname)
        return []

    const { hostname } = window.location

    if (hostname === 'localhost' || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname))
        return []

    const parts = hostname.split('.')

    return parts
        .map((_, index) => parts.slice(index).join('.'))
        .filter(domain => domain.includes('.'))
}

/**
 * deleteCookie
 * @param {string} cookieName Cookie name
 * @param {string=} domain Cookie domain
 */
function deleteCookie(cookieName, domain) {
    const domainAttribute = domain ? `; domain=${domain}` : ''

    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttribute}; SameSite=Lax`
}

/**
 * deleteGoogleAnalyticsCookies
 */
export function deleteGoogleAnalyticsCookies() {
    if (typeof document === 'undefined')
        return

    const cookieNames = document.cookie
        .split(';')
        .map(cookie => cookie.trim().split('=')[0])
        .filter(isGoogleAnalyticsCookieName)

    cookieNames.forEach(cookieName => {
        deleteCookie(cookieName)

        getCookieDomainCandidates().forEach(domain => {
            deleteCookie(cookieName, domain)
            deleteCookie(cookieName, `.${domain}`)
        })
    })
}

/**
 * isGoogleAnalyticsDebugMode
 * @returns {boolean} Is Google Analytics debug mode enabled
 */
function isGoogleAnalyticsDebugMode() {
    return process.env.REACT_APP_ENV === 'development'
}

/**
 * initGoogleAnalyticsDataLayer
 */
function initGoogleAnalyticsDataLayer() {
    window.dataLayer = window.dataLayer || []

    if (typeof window.gtag !== 'function')
        window.gtag = function gtag() {
            // Keep the official gtag.js signature for queued commands.
            // eslint-disable-next-line prefer-rest-params
            window.dataLayer.push(arguments)
        }
}

/**
 * updateGoogleAnalyticsConsent
 * @param {boolean} isGranted Is Google Analytics consent granted
 */
export function updateGoogleAnalyticsConsent(isGranted) {
    if (!isGranted)
        deleteGoogleAnalyticsCookies()

    if (typeof window === 'undefined' || typeof window.gtag !== 'function')
        return

    window.gtag('consent', 'update', {
        analytics_storage: isGranted ? 'granted' : 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
    })
}

/**
 * trackGoogleAnalyticsPageView
 * @param {string} measurementId Google Analytics measurement id
 * @param {string} pagePath Page path
 */
export function trackGoogleAnalyticsPageView(measurementId, pagePath) {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function' || !measurementId)
        return

    window.gtag('event', 'page_view', {
        send_to: measurementId,
        page_path: pagePath,
        page_location: `${window.location.origin}${pagePath}`,
        page_title: document.title,
        debug_mode: isGoogleAnalyticsDebugMode(),
    })
}

/**
 * loadGoogleAnalytics
 * @param {string} measurementId Google Analytics measurement id
 * @param {boolean} hasAnalyticsConsent Is Google Analytics consent granted
 */
export default function loadGoogleAnalytics(measurementId, hasAnalyticsConsent = false) {
    if (typeof window === 'undefined' || !measurementId)
        return

    initGoogleAnalyticsDataLayer()

    if (isGoogleAnalyticsLoaded)
        return

    window.gtag('consent', 'default', {
        analytics_storage: hasAnalyticsConsent ? 'granted' : 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
    })

    if (!document.getElementById(GOOGLE_ANALYTICS_SCRIPT_ID)) {
        const script = document.createElement('script')
        script.async = true
        script.id = GOOGLE_ANALYTICS_SCRIPT_ID
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
        document.head.appendChild(script)
    }

    window.gtag('js', new Date())
    window.gtag('config', measurementId, {
        send_page_view: false,
        debug_mode: isGoogleAnalyticsDebugMode(),
    })

    isGoogleAnalyticsLoaded = true
}
